import { createGnarClient, type GnarClient, MemoryGnarClient } from '@grammarly/gnar-app-client'
import type { EventProps } from '@grammarly/gnarclientweb'

import { type User } from '@lib/services/user/user'

// import { Cookie, getCookie } from '@lib/utils/cookies'
import { getConfig } from '@config/index'

import { APP_VERSION, GNAR_CLIENT_ID } from '@constants/globals'
import { IS_BROWSER } from '@constants/globals'

import { GnarSpec } from './spec'
import { GnarSpecWrapper } from './spec/wrapper'

const { tracking, domains } = getConfig()

let eventExtraProps = {}

const bindExtraProps = (client: GnarClient): GnarClient => {
  const originalTrack: (props: EventProps) => Promise<void> = client.track.bind(client)
  client.track = (props: EventProps) => originalTrack({ ...props, ...eventExtraProps })
  return client
}

const stripDot = (d: string) => (d.startsWith('.') ? d.slice(1) : d)

export const extendGnarEventsWith = (props: Omit<EventProps, 'eventName'>): EventProps => {
  eventExtraProps = { ...props, ...eventExtraProps }
  return eventExtraProps as EventProps
}

const resolveCookieDomain = (hostname: string): string | undefined => {
  if (!IS_BROWSER) return undefined

  if (hostname === 'localhost' || hostname === '127.0.0.1') return undefined

  // For ephemeral envs, qa, preprod directly accessing super-funnel, sh-web-server
  if (hostname.endsWith(stripDot(domains.controlPlaneCookie))) {
    return domains.controlPlaneCookie
  }
  // For qa, preprod, prod when hitting via cloudfront
  if (hostname.endsWith(stripDot(domains.cookie))) {
    return domains.cookie
  }

  return undefined
}

export const setGnarUser = (user: User) => {
  // Don't set user if it's None (anonymous/unauthenticated)
  if (user.isNone) {
    return
  }

  gnarClient.setUserWithParams(user.id, {
    isTest: user.isTest,
  })
}

const makeClient = (): GnarClient => {
  if (!IS_BROWSER) return new MemoryGnarClient()

  const host = window.location.hostname

  const cookieDomain = resolveCookieDomain(host)

  if (!cookieDomain) {
    return new MemoryGnarClient()
  }

  return bindExtraProps(
    createGnarClient(tracking.gnar.url, GNAR_CLIENT_ID, APP_VERSION, cookieDomain),
  )
}

export const gnarClient = makeClient()
export const gnarSpec = new GnarSpecWrapper(new GnarSpec(gnarClient))

export { type EventProps } from '@grammarly/gnarclientweb'
